import { Component } from '@angular/core';
import { FormControl, FormGroup, UntypedFormControl } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { Subject, BehaviorSubject, combineLatest, tap, startWith, withLatestFrom, map } from 'rxjs';
import { PageSize, IGridState, IBaseSearchRequest } from 'src/app/core/models/base';
import { AppStateInterface } from 'src/app/stores/global/appState.interface';
import { ICreateTimeSupplementRequest, ITimeSupplement, IUpdateTimeSupplementRequest } from '../time-supplement';
import { allTimeSupplementsSelector, createTimeSupplement, deleteTimeSupplement, searchTimeSupplements, selectTimeSupplementsFeature, timeSupplementsTotalSelector, updateTimeSupplement } from 'src/app/stores/global/time-supplements/time-supplements.store';
import { cancelIcon, editToolsIcon, pencilIcon, plusIcon, saveIcon, trashIcon } from '@progress/kendo-svg-icons';
import { debug, RxJsLoggingLevel } from 'src/app/shared/rxjs-operators/debug-operator';
import { flexibleTimeValidator } from 'src/app/shared/validators/timeStringValidator.validator';

@Component({
    selector: 'app-time-supplements',
    templateUrl: './time-supplements.component.html',
    styleUrl: './time-supplements.component.scss',
    standalone: false
})
export class TimeSupplementsComponent {


  constructor(    
    private store: Store<AppStateInterface>
  ) {}

  // Static Variables
  private editedRowIndex: number;
  public formGroup: FormGroup;
  public addIcon = plusIcon;
  public editIcon = pencilIcon;
  public saveIcon = saveIcon;
  public deleteIcon = trashIcon;
  public cancelIcon = cancelIcon;
  showDeleteModal : string;
  sort: SortDescriptor[] = [
    {
      field: "name",
      dir: "asc",
    },
  ];

  //Getters
  get pageSizes(){
    return PageSize;
  }


  // (Behaviour)Subjects
  pageChangeSub$ = new Subject<PageChangeEvent>();
  createItemSub$ = new Subject<ICreateTimeSupplementRequest>();
  updateItemSub$ = new Subject<IUpdateTimeSupplementRequest>();
  deleteItemSub$ = new Subject<string>();
  gridState$ = new BehaviorSubject<IGridState>(
    {
      pageSize: this.pageSizes.Small,
      skip: 0,
    }
  );

  searchRequest$ = new BehaviorSubject<IBaseSearchRequest>({
    pageNumber: 0,
    pageSize: PageSize.Small,
    orderBy: ['timeFrom asc'] // only needed for gizmo projects
  });



  // View Model
  vm$ = combineLatest([
    this.store.pipe(select(allTimeSupplementsSelector))
      .pipe(debug(RxJsLoggingLevel.DEBUG,"timesupplemente")), //Change to actual Store
    this.store.pipe(select(timeSupplementsTotalSelector)), //Change to actual Store
    this.createItemSub$.pipe(
      tap(request => this.store.dispatch(createTimeSupplement({request: request}))),
      startWith(null),
    ),
    this.updateItemSub$.pipe(
      tap(request => this.store.dispatch(updateTimeSupplement({request: request}))),
      startWith(null),
    ),
    this.deleteItemSub$.pipe(
      tap(id => this.store.dispatch(deleteTimeSupplement({id}))),
      startWith(null),
    ),
    this.gridState$,
    this.pageChangeSub$.pipe(
      withLatestFrom(this.searchRequest$),
        map(([pageChangeEvent, searchRequest]) => ({
          pageChangeEvent,
          searchRequest,
      })),
      tap(data => {
        const pageNumber = Math.round(
          (data.pageChangeEvent.skip + data.pageChangeEvent.take) /
          data.pageChangeEvent.take
        );
        this.gridState$.next({ skip: data.pageChangeEvent.skip, pageSize: data.pageChangeEvent.take});
        this.searchRequest$.next({
          ...data.searchRequest,
          pageNumber: pageNumber,
          pageSize: data.pageChangeEvent.take,
        });
      }),
      startWith({skip: 0, take: 10})
    ),
    this.searchRequest$.pipe(
      tap(request => this.store.dispatch(searchTimeSupplements({request})))
    )
  ]).pipe(
    map(([
      timeSupplements,
      total,
      createItemSub,
      updateItemSub,
      deleteItemSub,
      gridState,
      pageChangeSub,
      searchRequest
    ]) => ({
      timeSupplements,
      total,
      createItemSub,
      updateItemSub,
      deleteItemSub,
      gridState,
      pageChangeSub,
      searchRequest
    }))
  );

  ngOnInit(): void {}

  createFormGroup(timeSupplement : ITimeSupplement = null){
    return new FormGroup({
      id: new FormControl(timeSupplement?.id ?? null),
      validOnMonday: new FormControl(timeSupplement?.validOnMonday ?? false),
      validOnTuesday: new FormControl(timeSupplement?.validOnTuesday ?? false),
      validOnWednesday: new FormControl(timeSupplement?.validOnWednesday ?? false),
      validOnThursday: new FormControl(timeSupplement?.validOnThursday ?? false),
      validOnFriday: new FormControl(timeSupplement?.validOnFriday ?? false),
      validOnSaturday: new FormControl(timeSupplement?.validOnSaturday ?? false),
      validOnSunday: new FormControl(timeSupplement?.validOnSunday ?? false),
      timeFrom: new FormControl(timeSupplement?.timeFromObj.toStringShort() ?? null, flexibleTimeValidator()),
      timeTo: new FormControl(timeSupplement?.timeToObj.toStringShort() ?? null, flexibleTimeValidator()),
      percentage: new FormControl(timeSupplement?.percentage ?? 0),
    });
  }

  cancelHandler({ sender, rowIndex }) {
    this.closeEditor(sender, rowIndex);
    this.formGroup = null;
  }

  addHandler({ sender }) {
    this.closeEditor(sender);

    this.formGroup = this.createFormGroup();

    sender.addRow(this.formGroup);
  }

  editHandler({ sender, rowIndex, dataItem }) {
    this.closeEditor(sender);

    this.formGroup = this.createFormGroup(dataItem);

    this.editedRowIndex = rowIndex;

    sender.editRow(rowIndex, this.formGroup);
  }

  saveHandler(event){
    if(this.formGroup.value.id == null){
      this.createItemSub$.next({...this.formGroup.value})
    } else {
      this.updateItemSub$.next({...this.formGroup.value})
    }

    this.closeEditor(event.sender);
  }

  deleteHandler(event){
    this.deleteItemSub$.next(event.dataItem.id)
    this.closeEditor(event.sender);
  }

  sortChange(sort: SortDescriptor[], searchRequest: IBaseSearchRequest): void {
    // this is needed for gizmo projects
    this.sort = sort;
    this.searchRequest$.next({...searchRequest, orderBy: sort.map(x => `${x.field} ${x.dir}`)})
  }

  onDelete(id: string) {
    this.deleteItemSub$.next(id);
    this.showDeleteModal = null;
  }

  private closeEditor(grid, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }
}
